import { GeoJSONFeature, GeoJSONLineString } from 'ol/format/GeoJSON';
import Axios from 'axios'
import _ from "lodash";
import { getDistance, getLength } from 'ol/sphere';
import LineString from 'ol/geom/LineString';
import { latLngProjection } from './Renderer';
import CryptoJS from 'crypto-js'
import ElevDataPoint from './ElevDataPoint';
import ElevationChartHoverIndicator from './ElevationChartHoverIndicator';
import ElevationChartHelper from './ElevationChartHelper';

const elevationDataCache = 'segment-elevations'

// If you need to expire all caches, perhaps because the cached data has changed, 
// change this version number. Existing caches that don't match this will be destrpyed
// and recreated:
//
//const cacheVersion = "v1 Jan-2022"    // original cache, early Jan 2022
//const cacheVersion = "v2 27-Jan-2022" // changed cached data from SegmentElevationInfoElement[] to ElevDataPoint[]
//const cacheVersion = "v3 27-Jan-2022" // cached data is now "smoothed" before caching
//const cacheVersion = "v4 28-Jan-2022" // cached data is no longer "smoothed" before caching, and now gets smoothed after "simplify()"
//const cacheVersion = ""               // cache version is now applied as a salt to the hash key, initially a null string as current cache data remains valid
const cacheVersion   = "v5 02 Feb-2022" // cached data is now "smoothed" before caching (again)

export default class ElevationDataHelper {

    static async clearCache() {
        await caches.delete(elevationDataCache)
    }
 
    static async getElevationData(segment: GeoJSONFeature, elevationUrl: string): Promise<ElevDataPoint[]>  {

        const hash = cacheVersion + CryptoJS.MD5(JSON.stringify(segment)).toString(CryptoJS.enc.Base64)
        const cache = await caches.open(elevationDataCache)
        
        const cachedData = await cache.match(hash)

        if(cachedData) {
            const elements = await cachedData.json() as ElevDataPoint[]
            return elements
        }
        
        else {
            const augmentedSegment = ElevationDataHelper.interpolateGaps(segment) 
            const mapCoords = (augmentedSegment.geometry as GeoJSONLineString).coordinates
            const elevationData = await Axios.post<number[]>(`${elevationUrl}/height`, augmentedSegment) // TODO see "DEEPCLONE", below
            if(elevationData.data.length !== mapCoords.length) throw new Error('Height server did not return correct number of results.')

            let elements: ElevDataPoint[] = []
            let totDist = 0
            for(let i = 0; i < elevationData.data.length; i++) {
                if (i > 0) totDist += getLength(new LineString(mapCoords.slice(i-1, i+1)), { projection: latLngProjection }) // end param of slice is exclusive
                elements.push({x: totDist, y: elevationData.data[i], mapPosition: mapCoords[i], gradient: 0})
            }

            elements = this.smoothMast3(elements)

            cache.put(hash, new Response(JSON.stringify(elements)))
            return elements
        }
    }

    // Segments that were created by ATCF routing, or imported from GPX files recorded on
    // GPS devices, may contain trackpoints that are a long way apart. That is fine for navigation
    // routing, but lacks detail for the elevation profile (will appear as a straight line ramp
    // between the trackpoints), so interpolate additional trackpoints in the gaps for the 
    // dataset displayed on the elevation chart. Now that we have "trackpoint editing", you can
    // see these gaps if you look for them.
    // 
    // We don't add new points unless they will make an appreciable difference: we don't add any 
    // interpolated points unless the gap is big enough  for at least <minToAdd> points. The additional 
    // trackopints are in a straighht line over the ground, evenly spaced at a <separation> that 
    // roughly matches the granularity of the elevation data.
    //
    // The function returns a *new* segment rather than adding trackpoints to an existing one, 
    // so that the original track remains unchanged. The new track is only used for elevation profiling,
    // so there is no change in the number of trackpoints that may be exported for use in a GPS
    // navigation device.
    //
    // TODO DEEPCLONE if the height service can be refactored to accept a
    // a Position[] as its argument instead of a GeoJSONFeature, then the code below can be simplified to just
    // return "InterpolatedTrack" instead of wrapping it up in a deep clone of the original segment (and could
    // accept "segment.geometry.coordinates:Position[]" as it's input argument, instead of "segment: GeoJSONFeature")
    static separation = 18 // approx distance betwween interpolated points in metres
    static interpolateGaps(segment: GeoJSONFeature): GeoJSONFeature { 
        const minToAdd = 2  // not worth adding points unless the gap is big enough for this many
        let numPointsInserted = 0

        const originalTrack = (segment.geometry as GeoJSONLineString).coordinates
        const interpolatedTrack = [originalTrack[0]]

        for (let i=0, j=1; j < originalTrack.length; i++, j++) {
            const numPointsToInsert = Math.floor( getDistance(originalTrack[i], originalTrack[j]) / this.separation )
            if (numPointsToInsert >= minToAdd) {
                const dx = ( originalTrack[j][0] - originalTrack[i][0] ) / (numPointsToInsert+1)
                const dy = ( originalTrack[j][1] - originalTrack[i][1] ) / (numPointsToInsert+1)
                for (let s=1; s <= numPointsToInsert; s++) {
                    interpolatedTrack.push ([originalTrack[i][0] + s*dx, originalTrack[i][1] + s*dy])
                }
            }
            interpolatedTrack.push (originalTrack[j])
            numPointsInserted += numPointsToInsert
        }

        if (numPointsInserted == 0) return segment 

        const augmentedSegment = _.cloneDeep(segment)
        const augmentedGeom = augmentedSegment.geometry as GeoJSONLineString
        augmentedGeom.coordinates = interpolatedTrack

        return augmentedSegment
    }
     
    // MAST-3: Macfarlane's Averaged Smoothing Technique (over 3 points), with TDW (Taylor's Distance Weighting)
    // Smooth the elevation data by averaging the elevation at each point with its adjacent neighbours on each side:
    //      elevation at p = avg of unsmoothed elevation at { pl, p, pr }
    //          where pl, pr =  elev at left,right of p
    // The average is weighted so that the original elevation at each point has more significance than its neighbours.
    // The weighting for the neighbouring points on each side decays by a factor of 0.5 for every <separation> distance 
    // (that is roughly the granularity of the elevation data) so that, if the weighting for the centre point is 2, and
    // the separation distance is 18m:
    //      the weightings are {1.00, 2, 1.00} if the points are spaced at {18m, 18m}
    //      the weightings are {0.50, 2, 0.25} if the points are spaced at {36m, 54m}
    static smoothMast3 (allPoints: ElevDataPoint[]): ElevDataPoint[] {
        // Remove duplicates - the last point in each segment repeats as the first point in  the next segment, with the same x-coord (distance along track)
        // which appears as a step in the chart when smoothed. If we do simplify() before smooth() these will already be removed, but not if the other way round
        const points = [allPoints[0]];
        for (let i=1; i < allPoints.length; i++) {
            if (allPoints[i].x != allPoints[i-1].x) points.push(allPoints[i]);
        }

        const cweight = 2 // weighting for the y value in the centre of 3 points 

        const npoints = points.length
        if (npoints > 1) {
            let yl = 0, yc=0, yr=0 ; yc = yr = points[0].y  // (yl,yc,yr => y at [i-1], [i], [i+1], ie left, centre, right)
            let xl = 0, xc=0, xr=0 ; xc = xr = points[0].x  // (xl,xc,xr => x at [i-1], [i], [i+1], ie left, centre, right)
            for (let i=0, j=1; i < npoints; i++, j++) {
                yl = yc; yc = yr; yr = (j < npoints) ? points[j].y : points[i].y
                xl = xc; xc = xr; xr = (j < npoints) ? points[j].x : points[i].x
                if (isNaN(yc)) continue; // preseve NaN values indicating sections of track outside visible map area
                const l = isNaN(yl) ? yc : yl // the dataset won't include multiple consecutive NaNs
                const r = isNaN(yr) ? yc : yr // the dataset won't include multiple consecutive NaNs
                const lweight = isNaN(xl) ? 1 : cweight * Math.pow (2, (xl - xc) / ElevationDataHelper.separation) // NB the exponent is -ve, so that 2^N < 1
                const rweight = isNaN(xr) ? 1 : cweight * Math.pow (2, (xc - xr) / ElevationDataHelper.separation) // NB the exponent is -ve, so that 2^N < 1
                points[i].y = ( (lweight * l) + (cweight * yc) + (rweight * r) ) / (lweight + cweight + rweight)
            }
        }

        return points;
    }

}

